import {FormInstance} from 'antd/es/form';
import {
    omitBy,
    get,
    isEmpty,
    isFinite,
    isNaN,
    isNumber,
    isObject,
    isString,
} from 'lodash-es';
import {Key} from 'react';

import {TTruthy} from 'shared/types/generics';

import {TFieldData} from '../types';
import {escapeHtml} from './escape-html';

export type {TRequestPromise} from './request';
export {performRequest, downloadFile} from './request';
export {convertToFormData} from './convert-to-form-data';
export {showMessageFromResponse} from './show-message-from-response';
export {showMessage} from './notifications';
export {isString, isObject, isNumber};
export {getTablePaginationDefaultConfig} from './table';
export {isOracleBoolType} from './is-oracle-bool-type';
export {normalizeFieldMaxLength} from './field-max-length';

export {escapeHtml};

export const {isArray} = Array;

export const prettifyXml = (sourceXml: string) => {
    const xmlDoc = new DOMParser().parseFromString(sourceXml, 'application/xml');
    const xsltDoc = new DOMParser().parseFromString([
        '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
        '  <xsl:strip-space elements="*"/>',
        '  <xsl:template match="para[content-style][not(text())]">',
        '    <xsl:value-of select="normalize-space(.)"/>',
        '  </xsl:template>',
        '  <xsl:template match="node()|@*">',
        '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
        '  </xsl:template>',
        '  <xsl:output indent="yes"/>',
        '</xsl:stylesheet>',
    ].join('\n'), 'application/xml');

    const xsltProcessor = new XSLTProcessor();
    xsltProcessor.importStylesheet(xsltDoc);
    const resultDoc = xsltProcessor.transformToDocument(xmlDoc);
    const resultXml = new XMLSerializer().serializeToString(resultDoc);
    return resultXml;
};

export const setFieldsValueUntouched = (form: FormInstance, values: {[index: string]: any}) => {
    const valuesList: TFieldData[] = Object.entries(values).map(([index, value]) => ({
        name: index,
        value,
        touched: false,
    }));
    form.setFields(valuesList);
};

export const makeFormFieldsUntouched = (form: FormInstance) => {
    const currentFields = form.getFieldsValue();
    setFieldsValueUntouched(form, currentFields);
};

export const omitEmptyLines = (obj?: {[key: string]: any}) => {
    if (!obj) return undefined;
    return omitBy(obj, v => {
        if (typeof v === 'string' && v === '') return true;
        return false;
    });
};

export const filterObject = (
    object: Object,
    callback: (key: Key, value: any) => boolean,
) => {
    if (!object) return undefined;
    return Object.fromEntries(Object.entries(object).filter(([key, value]) => callback(key, value)));
};

export const isEmptyObject = (v: any) => !Array.isArray(v) && typeof v === 'object' && !Object.keys(v).length;

export const isEmptyArray = (v: any) => Array.isArray(v) && isEmpty(v);

export const isObj = (v: unknown) => typeof v === 'object' && v !== null;

export const isEmptyArrayOrObject = (v: any) => isEmptyObject(v) || isEmptyArray(v);

export const getMapOfValues = <I extends Record<string, any>>(
    items: I[], fieldName: string = 'id',
): Record<string, I> => items
        .reduce((acc: Record<string, I>, currentItem: I) => {
            acc[currentItem[fieldName]] = currentItem;
            return acc;
        }, {});

export const isOverflown = (element?: HTMLElement | null) => {
    if (!element) return false;

    const {
        clientWidth, clientHeight, scrollWidth, scrollHeight,
    } = element;

    return scrollHeight > clientHeight || scrollWidth > clientWidth;
};

const createParam = (key: string, value: string | string[]): string => {
    if (isObject(value)) {
        const newValue: string[] = Object.values(value);
        return newValue.join('=');
    }

    return isArray(value)
        ? value.map((item: string) => `${key}=${item}`).join('&')
        : `${key}=${value}`;
};

export const createQueryParams = (object: Record<string, any>, url?: string) => {
    const keys = Object.keys(object);
    const unnecessaryValues = [null, undefined];
    const alreadyHasQuery = url?.includes('?');

    return keys
        .filter(key => !unnecessaryValues.includes(object[key]))
        .reduce((params: string, currentKey: string, currentIndex: number) => {
            const separator = currentIndex === 0 && !alreadyHasQuery ? '?' : '&';
            const newParam = `${separator}${createParam(currentKey, object[currentKey])}`;

            return `${params}${newParam}`;
        }, '');
};

export const isBlob = (value: any) => value instanceof Blob;

export const createOptionItemTransformer = (
    labelPath: string, valuePath: string, reserveLabelPath?: string,
) => <T extends {}>(record: T) => {
    let label = get(record, labelPath) as string;
    const value = get(record, valuePath) as string;

    if (!label && reserveLabelPath) {
        label = get(record, reserveLabelPath);
    }

    return {
        ...record,
        label,
        value,
    };
};

export const createOptionItemsTransformer = (
    labelPath: string, valuePath: string, reserveLabelPath?: string,
) => <T extends {}>(records: T[]) => {
    const transformOption = createOptionItemTransformer(labelPath, valuePath, reserveLabelPath);

    return records.map(transformOption);
};

export const saveLocalFile = (file: File) => {
    const fileUrl = URL.createObjectURL(file);

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.name;
    link.click();
};

export const isEnumInstance = <T extends object>(value: string | number, type: T): type is T => (
    Object.values(type).includes(value)
);

export {
    convertArrayToTwoLevelObject,
    replaceFirstElementInArray,
    flattenDeepArrayByField,
} from './arrays';

export const isNumeric = (value: any) => (
    !isNaN(parseFloat(value)) && isFinite(parseFloat(value))
);

const moveUnderscoreToEnd = (str:string | undefined) => {
    if (!str) {
        return '';
    }
    const nonUnderscores = str.replace('_', '');
    const hasUnderscores = str.length > nonUnderscores.length;

    return `${nonUnderscores}${hasUnderscores ? '_' : ''}`;
};

export const escapeRegExp = (characters: string | undefined) => (
    moveUnderscoreToEnd(characters).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Экранируем специальные символы
);

/**
 * Извлекает или формирует код раздела на основе имени сущности.
 * Если имя сущности уже начинается с "app.", возвращает его как есть.
 * Если нет, добавляет "app." перед именем сущности.
 *
 * @param {string} [entityName=''] - Имя сущности, для которой нужно извлечь или сформировать код раздела.
 * @returns {string} - Код раздела в формате `app.<имя_сущности>`.
 */
export const getPrefixedSection = (
    entityName: string = '',
) => (
    /^app\./.test(entityName)
        ? entityName
        : `app.${entityName}`
);

// ts-friendly equivalent of Boolean
export const isTruthy = <T>(value: T): value is TTruthy<T> => !!value;

/**
 * Функция для удаления ключей с значениями undefined из объекта.
 * @param obj - Исходный объект.
 * @returns Новый объект без ключей со значениями undefined.
 */
export function removeUndefinedKeys<T extends object>(obj: T): Partial<T> {
    // Создаем новый объект, в который будем добавлять ключи с допустимыми значениями
    const result: Partial<T> = {};

    // Получаем массив пар [ключ, значение] и проходимся по нему
    Object.entries(obj).forEach(([key, value]) => {
        // Если значение не undefined, добавляем его в новый объект
        if (value !== undefined) {
            result[key as keyof T] = value;
        }
    });

    return result;
}

/**
 * Возвращает функцию для определения контейнера всплывающего элемента (например, выпадающего списка или календаря),
 *
 * @param fixedDropdown - Определяет, нужно ли фиксировать контейнер всплывающего элемента к его родителю
 * (нужно ли сохранять позицию дропдауна).
 *   - `true` (по умолчанию): Возвращает функцию, которая указывает на родительский элемент (`trigger.parentNode`).
 *   - `false`: Возвращает undefined.
 * @param isFormWithTabs Логическое значение, указывающее, используется ли форма с вкладками. По умолчанию `false`.
 *  - `true`: проверяется наличие элемента с классом `'form-with-tabs'`.
 *  - `false`(по умолчанию): выбор контейнера основан только на значении `fixedDropdown`.
 *
 * @returns Функция, принимающая элемент-триггер (`trigger`) и возвращающая его родителя.
 * - Если `fixedDropdown = true`, возвращает родительский элемент триггера.
 * - Если `fixedDropdown = false` и `isFormWithTabs = true`, возвращает элемент с классом `'form-with-tabs'`.
 * - Если `fixedDropdown = false` и `isFormWithTabs = false`, возвращается `undefined`.
 *
 * ### Примеры использования:
 * ```tsx
 * С фиксированным контейнером
 * <Select getPopupContainer={getPopupContainer(true)} />
 *
 * Контейнер не будет зафиксирован
 * <Select getPopupContainer={getPopupContainer(false)} />
 *
 * Контейнер будет зафиксирован и будет корректно отображаться на форме с табами
 * <Select getPopupContainer={getPopupContainer(false, true)} />
 *
 * Без аргументов, используется поведение по умолчанию (true)
 * <Select getPopupContainer={getPopupContainer()} />
 * ```
 */
export const getPopupContainer = (
    fixedDropdown: boolean = true,
    isFormWithTabs: boolean = false,
): TGetPopupContainer => {
    if (isFormWithTabs) {
        return (fixedDropdown
            ? trigger => trigger.parentNode as HTMLElement
            : () => document.getElementsByClassName('form-with-tabs')[0] as HTMLElement);
    }

    return (fixedDropdown
        ? (trigger => trigger.parentNode as HTMLElement)
        : undefined);
};

type TGetPopupContainer = ((trigger: HTMLElement) => HTMLElement) | undefined;

/**
 * Безопасно удаляет HTML-теги из строки, возвращая только текстовое содержимое.
 * Использует DOMParser для разбора HTML, что предотвращает выполнение скриптов и других опасных элементов.
 *
 * @param {string} html - Строка с HTML-разметкой, которую нужно очистить
 * @returns {string} Текстовое содержимое без HTML-тегов или пустая строка, если разбор не удался
 * @example
 * stripHtmlSafe('<p>Hello <b>world</b>!</p>'); // "Hello world!"
 * stripHtmlSafe('<script>alert("XSS")</script>'); // ""
 */
export const stripHtmlSafe = (html: string): string => {
    // Создаем временный DOM-элемент для безопасного парсинга HTML
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

export const transformEntityNameToClassName = (entityName: string) => String(entityName?.split('.').join('-'));

/**
 * Форматирует число в денежный формат с двумя знаками после запятой согласно правилам локали 'ru-RU'.
 * Если входное значение не может быть преобразовано в число, возвращает "0,00".
 *
 * @param v - Значение для форматирования. Может быть числом, строкой, null, undefined или другим типом.
 * @returns Отформатированная строка числа с двумя десятичными знаками в соответствии с локалью 'ru-RU'.
 * Если значение не может быть преобразовано в число, возвращает "0,00".
 *
 * @example
 * formatMonetaryNumber(1234.5);     // "1 234,50"
 * formatMonetaryNumber(1000);       // "1 000,00"
 * formatMonetaryNumber('9876.54');  // "9 876,54"
 * formatMonetaryNumber('12,34');  // "12,34"
 * formatMonetaryNumber('invalid');  // "0,00"
 * formatMonetaryNumber(null);       // "0,00"
 * formatMonetaryNumber(undefined);  // "0,00"
 */
export const formatMonetaryNumber = (v: unknown): string => {
    const normalizedString = String(v)
        .replace(/\s/g, '')
        .replace(/,/g, '.');

    const num = parseFloat(normalizedString);
    const safeNum = Number.isFinite(num) ? num : 0;

    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(safeNum);
};

/**
 * Проверяет, является ли переданное значение одним из значений перечисления (enum).
 *
 * @template T - Объект перечисления (enum) в виде Record<string, unknown>
 * @param enumObj - Объект перечисления, значения которого нужно проверить
 * @param value - Проверяемое значение
 * @returns {value is T[keyof T]} - Возвращает true, если значение является элементом перечисления,
 * и сужает тип value до типа значений перечисления
 *
 * @example
 * enum Color {
 *   Red = 'RED',
 *   Green = 'GREEN',
 *   Blue = 'BLUE'
 * }
 *
 * const testValue: unknown = 'RED';
 * if (isEnumValue(Color, testValue)) { // Здесь тип testValue сужен до Color (т.е. 'RED' | 'GREEN' | 'BLUE')
 *   console.log(testValue); // Безопасный доступ
 * }
 */
export const isEnumValue = <T extends Record<string, unknown>>(
    enumObj: T,
    value: unknown,
): value is T[keyof T] => Object.values(enumObj).includes(value);

/**
 * Проверяет наличие свойства в объекте.
 *
 * @template T - Тип объекта для проверки
 * @template K - Тип ключа свойства
 * @param obj - Объект для проверки
 * @param key - Имя свойства для проверки
 * @returns true, если объект не null, является объектом и содержит указанное свойство
 *
 * @example
 * ```
 * const response = {data: {summaryMessage: 'Success'}};
 *
 * if (hasProperty(response.data, 'summaryMessage')){
 *     console.log(response.data.summaryMessage);
 * }
 * ```
 */
export const  hasProperty = <K extends PropertyKey>(
    obj: unknown,
    key: K
): obj is Record<K, unknown>  => obj !== null && typeof obj === 'object' && key in obj;